import React, { useState, useEffect } from 'react';
import { getProfile, updateProfile, changePassword } from '../mocks/api';
import { useAuth } from '../contexts/AuthContext';

export default function Profile() {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwMessage, setPwMessage] = useState('');
  const [pwError, setPwError] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await getProfile();
      setProfile(data);
      setForm({ firstName: data.firstName, lastName: data.lastName, email: data.email });
      setLoading(false);
    })();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    const updated = await updateProfile(form);
    setProfile(updated);
    updateUser(updated);
    setEditing(false);
    setMessage('Profile updated successfully.');
    setSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwMessage('');
    if (pwForm.newPw !== pwForm.confirm) {
      setPwError('New passwords do not match.');
      return;
    }
    setChangingPw(true);
    try {
      await changePassword(pwForm.current, pwForm.newPw);
      setPwMessage('Password changed successfully.');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      setPwError(err.message);
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) return <div className="loading">Loading profile...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>My Profile</h2>
      </div>

      {message && <div className="alert alert-success">{message}</div>}

      <div className="profile-grid">
        <div className="card">
          <h3>Personal Information</h3>
          {editing ? (
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>First Name</label>
                <input required value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input required value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="btn-group">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-row"><span className="label">Name:</span><span>{profile.firstName} {profile.lastName}</span></div>
              <div className="info-row"><span className="label">Email:</span><span>{profile.email}</span></div>
              <div className="info-row"><span className="label">Username:</span><span>{profile.username}</span></div>
              <div className="info-row"><span className="label">Role:</span><span className="badge">{profile.role}</span></div>
              <button className="btn btn-outline" onClick={() => setEditing(true)}>Edit Profile</button>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Change Password</h3>
          {pwMessage && <div className="alert alert-success">{pwMessage}</div>}
          {pwError && <div className="alert alert-error">{pwError}</div>}
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label>Current Password</label>
              <input required type="password" value={pwForm.current} onChange={e => setPwForm({ ...pwForm, current: e.target.value })} />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input required type="password" value={pwForm.newPw} onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input required type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={changingPw}>
              {changingPw ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        <div className="card">
          <h3>License Status</h3>
          <div className="profile-info">
            <div className="info-row"><span className="label">Plan:</span><span className="badge badge-active">{profile.license.plan}</span></div>
            <div className="info-row"><span className="label">Status:</span><span className={`badge badge-${profile.license.status}`}>{profile.license.status}</span></div>
            <div className="info-row"><span className="label">Expires:</span><span>{profile.license.expiresAt}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
